import type { SetupState, SupplierSetupState } from '@tandemone/types'
import {
  type LucideIcon,
  Beaker,
  Building2,
  ClipboardCheck,
  CreditCard,
  Factory,
  FileCheck,
  FileText,
  Globe,
  HardHat,
  Layers,
  Mail,
  ScrollText,
  TrendingUp,
  Users
} from 'lucide-react'

import { isBillingEnabled } from '@/api/config'

export type ChecklistSection =
  | 'due_diligence'
  | 'quality_assessment'
  | 'documents'
  | 'plan'
  | 'company_profile'

export type ChecklistItem = {
  id: string
  section: ChecklistSection
  title: string
  description: string
  icon: LucideIcon
  ctaHref: string
  settingsHref?: string
  complete: boolean
  disabled: boolean
  lockedReason?: string
  optional?: boolean
}

export type SectionInfo = {
  id: ChecklistSection
  title: string
  description: string
  href: string
  icon: LucideIcon
}

export const SUPPLIER_SECTIONS: ReadonlyArray<SectionInfo> = [
  {
    id: 'due_diligence',
    title: 'Due diligence',
    description:
      'Who you are and what you do. Buyers reference these for company evaluation.',
    href: '/onboarding/supplier/due-diligence',
    icon: Building2
  },
  {
    id: 'quality_assessment',
    title: 'Quality assessment',
    description:
      'How you run quality. Measurement capabilities and the six-section assessment.',
    href: '/onboarding/supplier/quality-assessment',
    icon: Beaker
  },
  {
    id: 'documents',
    title: 'Documents',
    description: 'Certifications and supporting documentation.',
    href: '/onboarding/supplier/documents',
    icon: FileText
  },
  {
    id: 'plan',
    title: 'Supplier Plan',
    description:
      'Pick the plan you start on. Nothing is billed until approval.',
    href: '/onboarding/supplier/plan',
    icon: CreditCard
  }
]

export type SectionProgress = {
  info: SectionInfo
  items: ChecklistItem[]
  complete: number
  total: number
  clickable: boolean
}

export function companyContactsDescription(isSupplier: boolean): string {
  return isSupplier
    ? 'The people buyers should reach out to.'
    : 'This information is not shared to Suppliers until you send an RFI.'
}

/** the company-level steps both roles fill, rendered as standalone cards above
 * the role sections. their `section` tag is unused, they don't belong to a
 * role section. (company type is chosen at signup, not here.)
 */
export function getSharedSteps(setup: SetupState): ChecklistItem[] {
  return [
    {
      id: 'company-details',
      section: 'company_profile',
      title: 'Company Details',
      description:
        'Legal name, website, headquarter location, and year founded.',
      icon: Building2,
      ctaHref: '/onboarding/company/details',
      settingsHref: '/settings/company-details',
      complete: setup.hasGeneralInfo,
      disabled: false
    },
    {
      id: 'company-contacts',
      section: 'company_profile',
      title: 'Company Contacts',
      description: companyContactsDescription(setup.isSupplier),
      icon: Users,
      ctaHref: '/onboarding/company/contacts',
      settingsHref: '/settings/company-contacts',
      complete: setup.hasContacts,
      disabled: false
    },
    /* settings nav lists these too, so the step only exists during onboarding. */
    ...(setup.isOnboarding && setup.needsWorkEmail
      ? [
          {
            id: 'work-email',
            section: 'company_profile' as const,
            title: 'Work Email',
            description:
              'We approve buyer accounts that use a company email address.',
            icon: Mail,
            ctaHref: '/onboarding/work-email',
            complete: false,
            disabled: false
          }
        ]
      : [])
  ]
}

export function getSupplierSections(setup: SetupState): SectionProgress[] {
  const items = getSupplierItems(setup.supplier, setup.hasGeneralInfo)
  /* the paywall ships dark, so a plan step nobody can act on must not hold
   * onboarding at 99%. */
  const sections = isBillingEnabled()
    ? SUPPLIER_SECTIONS
    : SUPPLIER_SECTIONS.filter(info => info.id !== 'plan')
  const billedThrough = setup.entitlements.billedThrough
  const billedNote = billedThrough
    ? `Billed through ${billedThrough.companyName}. There’s no plan to pick.`
    : null
  return sections.map(info => {
    const billedPlan = info.id === 'plan' && billedNote !== null
    const sectionItems = items
      .filter(i => i.section === info.id)
      .map(item =>
        billedPlan && billedNote
          ? { ...item, disabled: true, lockedReason: billedNote }
          : item
      )
    const requiredItems = sectionItems.filter(i => !i.optional)
    return {
      info:
        billedPlan && billedNote ? { ...info, description: billedNote } : info,
      items: sectionItems,
      complete: requiredItems.filter(i => i.complete).length,
      total: requiredItems.length,
      clickable: !billedPlan
    }
  })
}

function getSupplierItems(
  supplierSetup: SupplierSetupState | null,
  hasGeneralInfo = true
): ChecklistItem[] {
  const s = supplierSetup
  const qualitySkipped = s?.qualityAssessmentSkippedByCert ?? false

  return [
    /** due diligence: who you are and what you do. company information +
     * contacts are pulled out as shared top-level steps. */
    {
      id: 'supplier-policies',
      section: 'due_diligence',
      title: 'Policies',
      description:
        'HSE, Code of Ethics, Social and Labor, Disaster Management.',
      icon: ScrollText,
      ctaHref: '/onboarding/supplier/policies',
      complete: s?.hasPolicies ?? false,
      disabled: false
    },
    {
      id: 'supplier-workforce',
      section: 'due_diligence',
      title: 'Workforce',
      description:
        'Employee counts: corporate, engineering, production, quality.',
      icon: HardHat,
      ctaHref: '/onboarding/supplier/workforce',
      complete: s?.hasWorkforce ?? false,
      disabled: false
    },
    {
      id: 'supplier-operations',
      section: 'due_diligence',
      title: 'Capabilities',
      description: 'What manufacturing processes do you offer?',
      icon: Factory,
      ctaHref: '/onboarding/supplier/capabilities',
      complete: s?.hasOperations ?? false,
      disabled: false
    },
    {
      id: 'supplier-industries',
      section: 'due_diligence',
      title: 'Industries',
      description: 'What end-markets do you build for?',
      icon: Globe,
      ctaHref: '/onboarding/supplier/industries',
      complete: s?.hasIndustries ?? false,
      disabled: false
    },
    {
      id: 'supplier-sales',
      section: 'due_diligence',
      title: 'Sales history',
      description: 'Revenue range over the past three fiscal years.',
      icon: TrendingUp,
      ctaHref: '/onboarding/supplier/sales',
      settingsHref: '/settings/supplier/sales-history',
      complete: s?.hasSalesHistory ?? false,
      disabled: false
    },
    {
      id: 'supplier-locations',
      section: 'due_diligence',
      title: 'Manufacturing locations',
      description: 'Sites, building size, shifts, nearest shipping port.',
      icon: Layers,
      ctaHref: '/onboarding/supplier/locations',
      settingsHref: '/settings/supplier/manufacturing-locations',
      complete: s?.hasLocations ?? false,
      disabled: false
    },
    {
      id: 'supplier-equipment',
      section: 'due_diligence',
      title: 'Capital equipment',
      description: 'Machines and equipment available on the floor.',
      icon: ClipboardCheck,
      ctaHref: '/onboarding/supplier/equipment',
      settingsHref: '/settings/supplier/capital-equipment',
      complete: s?.hasCapitalEquipment ?? false,
      disabled: false
    },

    // quality assessment: how you run quality
    {
      id: 'supplier-quality-management',
      section: 'quality_assessment',
      title: 'Management',
      description: qualitySkipped
        ? 'Auto-completed by your certification.'
        : 'Quality policy, manual, management review, corrective action.',
      icon: FileCheck,
      ctaHref: '/onboarding/supplier/quality/management-requirements',
      complete: s?.hasQualityManagementRequirements ?? false,
      disabled: false
    },
    {
      id: 'supplier-quality-risk',
      section: 'quality_assessment',
      title: 'Risk',
      description: qualitySkipped
        ? 'Auto-completed by your certification.'
        : 'Contract review, risk assessment, document control, quality records.',
      icon: FileCheck,
      ctaHref: '/onboarding/supplier/quality/risk-assessment',
      complete: s?.hasQualityRiskAssessment ?? false,
      disabled: false
    },
    {
      id: 'supplier-quality-nonconformance',
      section: 'quality_assessment',
      title: 'Nonconformance',
      description: qualitySkipped
        ? 'Auto-completed by your certification.'
        : 'Nonconforming products, preventive maintenance, calibration, training.',
      icon: FileCheck,
      ctaHref: '/onboarding/supplier/quality/nonconformance-maintenance',
      complete: s?.hasQualityNonconformanceMaintenance ?? false,
      disabled: false
    },
    {
      id: 'supplier-measurement',
      section: 'quality_assessment',
      title: 'Measurement Capabilities',
      description: qualitySkipped
        ? 'Auto-completed by your certification.'
        : 'In-house and sub-contracted inspection equipment.',
      icon: Beaker,
      ctaHref: '/onboarding/supplier/measurement',
      complete: s?.hasMeasurementCapabilities ?? false,
      disabled: false
    },
    {
      id: 'supplier-quality-inspection',
      section: 'quality_assessment',
      title: 'Inspection',
      description: qualitySkipped
        ? 'Auto-completed by your certification.'
        : 'In-person inspections, job set-up, material certifications.',
      icon: FileCheck,
      ctaHref: '/onboarding/supplier/quality/inspection-measurement',
      complete: s?.hasQualityInspectionMeasurement ?? false,
      disabled: false
    },
    {
      id: 'supplier-quality-purchasing',
      section: 'quality_assessment',
      title: 'Purchasing',
      description: qualitySkipped
        ? 'Auto-completed by your certification.'
        : 'Purchasing, product identification, traceability, process validation.',
      icon: FileCheck,
      ctaHref: '/onboarding/supplier/quality/purchasing-supplier-selection',
      complete: s?.hasQualityPurchasingSupplierSelection ?? false,
      disabled: false
    },
    {
      id: 'supplier-quality-improvement',
      section: 'quality_assessment',
      title: 'Continual Improvement',
      description: qualitySkipped
        ? 'Auto-completed by your certification.'
        : 'Continual improvement, PPM/OTD metrics, customer scorecards.',
      icon: FileCheck,
      ctaHref: '/onboarding/supplier/quality/continual-improvement',
      complete: s?.hasQualityContinualImprovement ?? false,
      disabled: false
    },

    // documents: certifications and supporting media
    {
      id: 'supplier-documents',
      section: 'documents',
      title: 'Documents',
      description:
        'Upload your certifications and supporting media. Certs gate completion.',
      icon: FileText,
      ctaHref: '/onboarding/supplier/documents',
      complete: s?.hasCertificates ?? false,
      disabled: false
    },

    {
      id: 'supplier-plan',
      section: 'plan',
      title: 'Plan',
      /* the plan is billed to a company, so there has to be a company first. */
      description: hasGeneralInfo
        ? 'Choose the plan you start on. We bill it once your profile is approved.'
        : 'Complete Company Details first, then choose the plan you start on.',
      icon: CreditCard,
      ctaHref: '/onboarding/supplier/plan',
      complete: s?.hasPlan ?? false,
      disabled: !hasGeneralInfo,
      lockedReason: 'Complete Company Details first'
    }
  ]
}
